/**
 * 会社名検索の再検索クエリ生成と入力判定（#8796）
 *
 * @description
 *  すべて純関数。表示中の入力値は書き換えず、検索クエリ側だけを変換する（#8264 と同方針）。
 *  再検索チップは「提案」で自動適用しないため、外れても害はない前提で緩めの正規表現にしている。
 */

export type RetryQueryKind = 'strip_department' | 'strip_corporate_type' | 'normalize_width' | 'partial'

export interface RetryQuery {
  kind: RetryQueryKind,
  label: string,
  query: string,
}

/** 法人格（漢字・略記号・カナ／かな表記） */
const CORPORATE_TYPE_PATTERN = /株式会社|有限会社|合同会社|合資会社|合名会社|一般社団法人|一般財団法人|カブシキガイシャ|カブシキカイシャ|かぶしきがいしゃ|かぶしきかいしゃ|\(株\)|（株）|㈱|\(有\)|（有）|㈲/g

/** 会社名の後ろに付く支店・部署を表す末尾語 */
const DEPARTMENT_SUFFIX_PATTERN = /(支店|支社|営業所|工場|事業所|本部|本社|センター)$/

/** 支店名の前に付きやすい地域名（都道府県の接頭・広域・方角）。末尾語を落とした後にさらに落とす */
const REGION_PATTERN = /(北海道|青森|岩手|宮城|秋田|山形|福島|茨城|栃木|群馬|埼玉|千葉|東京|神奈川|新潟|富山|石川|福井|山梨|長野|岐阜|静岡|愛知|三重|滋賀|京都|大阪|兵庫|奈良|和歌山|鳥取|島根|岡山|広島|山口|徳島|香川|愛媛|高知|福岡|佐賀|長崎|熊本|大分|宮崎|鹿児島|沖縄|東北|関東|首都圏|中部|北陸|東海|近畿|関西|中国|四国|九州|東日本|西日本|東|西|南|北|中央)$/

/** 後株の直後に語が続く形（「日本製紙株式会社石巻工場」）。法人格の前に 2 文字以上の社名があるときだけ */
const TRAILING_AFTER_CORPORATE_TYPE_PATTERN = /^(.{2,}?(?:株式会社|有限会社|合同会社|\(株\)|（株）|㈱))(.+)$/

const WHITESPACE_PATTERN = /[\s\u3000]+/
const PREFECTURE_PATTERN = /^(東京都|北海道|(?:京都|大阪)府|.{2,3}県)/
const PHONE_SEPARATOR_PATTERN = /[\s\-‐−()（）+]/g
const CORPORATE_NUMBER_SEPARATOR_PATTERN = /[\s\-‐−]/g

const CORPORATE_NUMBER_LENGTH = 13
const LATIN_LIKE_MIN_LENGTH = 2
const PHONE_LIKE_MIN_LENGTH = 9
const PHONE_LIKE_MAX_LENGTH = 11
const MIN_QUERY_LENGTH = 2

/**
 * 法人格（株式会社・(株)・㈱ 等）を取り除く
 */
export function stripCorporateType(value: string): string {
  return value.replace(CORPORATE_TYPE_PATTERN, '').trim()
}

/**
 * 支店・部署名を取り除いて会社名だけにする。変換できないときは trim した入力をそのまま返す
 *
 * @description 優先順は 1. 空白区切りの先頭語 → 2. 後株の直後で切る → 3. 末尾の組織語（＋直前の地域名）を落とす
 */
export function stripDepartment(value: string): string {
  const trimmed = value.trim()

  const [head] = trimmed.split(WHITESPACE_PATTERN)
  if (head !== trimmed) return head

  const afterCorporateType = trimmed.match(TRAILING_AFTER_CORPORATE_TYPE_PATTERN)
  if (afterCorporateType) return afterCorporateType[1]

  const withoutSuffix = trimmed.replace(DEPARTMENT_SUFFIX_PATTERN, '')
  if (withoutSuffix === trimmed) return trimmed

  return withoutSuffix.replace(REGION_PATTERN, '')
}

/**
 * 全角英数・半角カナを NFKC で揃える
 */
export function normalizeWidth(value: string): string {
  return value.normalize('NFKC')
}

/**
 * 見つからなかった入力値から、ワンタップで試せる再検索クエリを生成する
 *
 * @description 入力と同じになるもの・2 文字未満・重複は除外。層別分析で最大群だった支店・部署名付加を先頭に出す
 */
export function buildRetryQueries(value: string): RetryQuery[] {
  const trimmed = value.trim()
  const queries: RetryQuery[] = []

  const push = (kind: RetryQueryKind, label: string, query: string) => {
    if (query.length < MIN_QUERY_LENGTH) return
    if (query === trimmed) return
    if (queries.some(q => q.query === query)) return
    queries.push({ kind, label, query })
  }

  const withoutDepartment = stripDepartment(trimmed)
  push('strip_department', `「${withoutDepartment}」で探す（支店・部署名を外す）`, withoutDepartment)

  const withoutCorporateType = stripCorporateType(trimmed)
  push('strip_corporate_type', `「${withoutCorporateType}」で探す（法人格を外す）`, withoutCorporateType)

  push('normalize_width', '全角・半角を揃えて探す', normalizeWidth(trimmed))

  const base = stripCorporateType(withoutDepartment)
  const partial = base.slice(0, Math.ceil(base.length / 2))
  if (partial.length < base.length) push('partial', `「${partial}」で部分一致`, partial)

  return queries
}

/**
 * 住所の先頭から都道府県名を取り出す。取れなければ空文字
 *
 * @description 検索 API の address は住所が取れない法人で null になる（gBizINFO の location 欠落）ため、null / undefined も受ける
 */
export function extractPrefecture(address?: string | null): string {
  if (!address) return ''

  const matched = address.match(PREFECTURE_PATTERN)

  return matched
    ? matched[1]
    : ''
}

/**
 * 英字だけで書かれた社名か（半角・全角どちらも対象）
 *
 * @description
 *  登記名がカタカナの会社は英字表記では検索に当たらない（実測: BizHint / BizReach は 0 件、
 *  ビズヒント / ビズリーチ は 1 件）。カタカナでの再入力を案内する条件に使う。
 *  日本語が混じる入力（「BizHint株式会社」等）は登記名も英字混じりのことがあるため対象外。
 */
export function isLatinLike(value: string): boolean {
  const compact = normalizeWidth(value).replace(/[\s.,&'’\-‐−()]/g, '')
  if (compact.length < LATIN_LIKE_MIN_LENGTH) return false

  return /^[A-Za-z]+[A-Za-z0-9]*$/.test(compact)
}

/**
 * 電話番号らしい入力か（区切りを除いて数字のみ・9〜11 桁）。13 桁は法人番号なので含めない
 */
export function isPhoneLike(value: string): boolean {
  const compact = normalizeWidth(value).replace(PHONE_SEPARATOR_PATTERN, '')
  if (!/^\d+$/.test(compact)) return false

  return compact.length >= PHONE_LIKE_MIN_LENGTH && compact.length <= PHONE_LIKE_MAX_LENGTH
}

/**
 * 入力から 13 桁の法人番号を取り出す。区切り（空白・ハイフン）と全角数字は許容。それ以外が混じれば null
 */
export function extractCorporateNumber(value: string): string | null {
  const compact = normalizeWidth(value).replace(CORPORATE_NUMBER_SEPARATOR_PATTERN, '')

  return new RegExp(`^\\d{${CORPORATE_NUMBER_LENGTH}}$`).test(compact)
    ? compact
    : null
}

/**
 * 入力した会社名と同じ名前の候補がちょうど 1 件あれば返す。なければ null
 *
 * @description
 *  候補を選ばずに会社名欄を離れた人を自動で確定させるための判定（#9060）。
 *  部分一致で 1 件だけヒットした別会社を確定させないよう、名前の一致を条件にする。
 *  同名の会社が複数あるときはどれか決められないので確定しない。
 *  全角半角・大文字小文字・空白の違いだけは同じ名前と見なす
 */
export function findExactNameMatch(value: string, candidates: CompanySearchResults): CompanySearchInfo | null {
  const normalize = (name: string) => normalizeWidth(name).toLowerCase().replace(new RegExp(WHITESPACE_PATTERN, 'g'), '')
  const query = normalize(value)
  if (!query) return null

  // 外部 API は会社名を欠いた候補を返しうる（型は string だが name・name_en とも無いと null）
  const matched = candidates.filter(candidate => normalize(candidate.company_name ?? '') === query)

  return matched.length === 1
    ? matched[0]
    : null
}
